import config, { initConfig } from './config.js';

// Initialize the application
async function initializeApp() {
    try {
        console.log('Starting application initialization...');
        
        // Initialize configuration
        console.log('Loading configuration...');
        const configData = await initConfig();
        console.log('Configuration loaded:', {
            supabaseUrl: configData.supabaseUrl ? 'present' : 'missing',
            supabaseAnonKey: configData.supabaseAnonKey ? 'present' : 'missing',
            apiUrl: configData.apiUrl ? 'present' : 'missing'
        });

        // Wait for Supabase client to be available
        console.log('Waiting for Supabase client...');
        let attempts = 0;
        while (!window.supabaseClient && attempts < 50) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }

        if (!window.supabaseClient) {
            throw new Error('Failed to initialize Supabase client');
        }

        // Store Supabase client in current state
        currentState.supabaseClient = window.supabaseClient;
        console.log('Supabase client initialized');

        // Initialize Stripe
        console.log('Initializing Stripe...');
        currentState.stripe = Stripe(window.VITE_STRIPE_PUBLIC_KEY);
        console.log('Stripe initialized');
        
        // Set up event listeners
        console.log('Setting up event listeners...');
        setupEventListeners();
        
        // Set up auth state change listener
        currentState.supabaseClient.auth.onAuthStateChange((event, session) => {
            console.log('Auth state changed:', event, session?.user?.email);
            handleAuthStateChange(session);
        });
        
        // Get current session
        const { data: { session } } = await currentState.supabaseClient.auth.getSession();
        if (session) {
            console.log('Session found:', session.user.email);
            await handleAuthStateChange(session);
        } else {
            console.log('No session found');
            await handleAuthStateChange(null);
        }
        
        // Start fetching data
        console.log('Starting initial data fetch...');
        await Promise.all([
            fetchCurrentPrice(),
            fetchLatestAd(),
            fetchPreviousAds()
        ]);
        
        console.log('Application initialization completed successfully');
    } catch (error) {
        console.error('Failed to initialize application:', error);
        // Show error message to user
        const errorMessage = document.createElement('div');
        errorMessage.className = 'alert alert-danger';
        errorMessage.role = 'alert';
        errorMessage.style.position = 'fixed';
        errorMessage.style.top = '20px';
        errorMessage.style.left = '50%';
        errorMessage.style.transform = 'translateX(-50%)';
        errorMessage.style.zIndex = '9999';
        errorMessage.innerHTML = `
            <strong>Error:</strong> Failed to initialize application. 
            Please refresh the page or contact support if the problem persists.
            <br>
            <small class="text-muted">${error.message}</small>
        `;
        document.body.appendChild(errorMessage);
        
        // Remove the error message after 10 seconds
        setTimeout(() => {
            errorMessage.remove();
        }, 10000);
    }
}

// Start the application
initializeApp();

// Global state
let currentState = {
    price: null,
    nextPrice: null,
    currentAd: null,
    user: null,
    viewers: 0,
    updateInterval: null,
    isRegistering: false,
    supabaseClient: null,
    channel: null,
    currentPrice: null,
    card: null,
    priceId: null,
    priceWindow: null,
    modalPriceInterval: null
};

// DOM Elements
const elements = {
    // Price display elements
    priceAmount: document.getElementById('price-amount'),
    nextPrice: document.getElementById('next-price'),
    priceTimer: document.getElementById('price-timer'),
    
    // Ad elements
    currentAd: document.getElementById('current-ad'),
    adText: document.getElementById('ad-text'),
    adLink: document.getElementById('ad-link'),
    postAdBtn: document.getElementById('post-ad-btn'),
    adModal: new bootstrap.Modal(document.getElementById('ad-modal')),
    adInput: document.getElementById('ad-input'),
    adUrl: document.getElementById('ad-url'),
    charCount: document.getElementById('char-count'),
    submitAdBtn: document.getElementById('submit-ad-btn'),
    
    // Auth elements
    authButtons: document.getElementById('auth-buttons'),
    loginBtn: document.getElementById('login-btn'),
    registerBtn: document.getElementById('register-btn'),
    userMenu: document.getElementById('user-menu'),
    userEmail: document.getElementById('user-email'),
    authModal: new bootstrap.Modal(document.getElementById('authModal')),
    authForm: document.getElementById('auth-form'),
    authModalTitle: document.getElementById('auth-modal-title'),
    authDescription: document.getElementById('auth-description'),
    authSubmitBtn: document.getElementById('auth-submit-btn'),
    toggleAuthMode: document.getElementById('toggle-auth-mode'),
    registerFields: document.getElementById('register-fields'),
    emailInput: document.getElementById('email'),
    passwordInput: document.getElementById('password'),
    confirmPasswordInput: document.getElementById('confirm-password'),
    googleAuthBtn: document.getElementById('google-auth-btn'),
    
    // Profile elements
    viewProfileBtn: document.getElementById('view-profile-btn'),
    myAdsBtn: document.getElementById('my-ads-btn'),
    earningsBtn: document.getElementById('earnings-btn'),
    logoutBtn: document.getElementById('logout-btn'),
    
    // Stats elements
    viewerCount: document.getElementById('viewer-count'),
    
    // Previous ads elements
    previousAdsList: document.getElementById('previous-ads-list'),
};

// Price Calculation Functions
function calculateDecayedPrice(lastPaidPrice, timeElapsed) {
    return config.basePrice + (lastPaidPrice - config.basePrice) / (1 + config.decayRate * timeElapsed);
}

function calculateNextPrice(currentPrice) {
    return currentPrice * config.priceMultiplier;
}

// UI Update Functions
function updatePriceDisplay(price, nextPrice) {
    elements.priceAmount.textContent = price.toFixed(2);
    elements.nextPrice.textContent = nextPrice.toFixed(2);
    
    // Update modal price if it's open
    const modalPrice = document.getElementById('modal-price');
    if (modalPrice) {
        const oldPrice = parseFloat(modalPrice.textContent);
        modalPrice.textContent = price.toFixed(2);
        
        // Show price change alert if price has increased
        if (oldPrice && price > oldPrice) {
            const priceAlert = document.getElementById('price-change-alert');
            const newPriceAmount = document.getElementById('new-price-amount');
            if (priceAlert && newPriceAmount) {
                newPriceAmount.textContent = price.toFixed(2);
                priceAlert.classList.remove('d-none');
                elements.submitAdBtn.disabled = true;
                
                // Hide alert and re-enable button after 5 seconds
                setTimeout(() => {
                    priceAlert.classList.add('d-none');
                    elements.submitAdBtn.disabled = false;
                }, 5000);
            }
        }
    }
}

function updateAdDisplay(ad) {
    if (!ad) {
        elements.adText.textContent = 'Ad loading...';
        elements.adLink.href = '#';
        return;
    }
    
    elements.adText.textContent = ad.text;
    elements.adLink.href = ad.url;
    elements.adText.classList.add('fade-in');
    setTimeout(() => elements.adText.classList.remove('fade-in'), 500);
}

function startPriceUpdateTimer(initialTimeLeft) {
    if (currentState.updateInterval) {
        clearInterval(currentState.updateInterval);
    }

    let timeLeft = initialTimeLeft;
    
    function updateTimer() {
        const minutes = Math.floor(timeLeft / 60);
        const seconds = timeLeft % 60;
        elements.priceTimer.querySelector('span').textContent = 
            `${minutes}:${seconds.toString().padStart(2, '0')}`;
        
        if (timeLeft === 0) {
            fetchCurrentPrice();
            timeLeft = 300;
        } else {
            timeLeft--;
        }
    }

    updateTimer();
    currentState.updateInterval = setInterval(updateTimer, 1000);
}

// New function to fetch the latest ad
async function fetchLatestAd() {
    try {
        console.log('Fetching latest ad...');
        const { data: ads, error } = await currentState.supabaseClient
            .from('ads')
            .select('text, url')
            .eq('active', true)
            .order('created_at', { ascending: false })
            .limit(1);

        if (error) {
            console.error('Error fetching latest ad:', error);
            return;
        }

        if (ads && ads.length > 0) {
            updateAdDisplay(ads[0]);
        } else {
            updateAdDisplay(null);
        }
    } catch (error) {
        console.error('Failed to fetch latest ad:', error);
        updateAdDisplay(null);
    }
}

// API Functions
async function fetchCurrentPrice() {
    try {
        console.log('Fetching current price...');
        const response = await fetch(`${config.apiUrl}/api/calculate-price`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            mode: 'cors',
            credentials: 'omit'
        });

        if (!response.ok) {
            console.error('Price fetch failed:', response.status, response.statusText);
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Price data received:', data);
        
        if (data.error) {
            console.error('Error in price data:', data.error);
            throw new Error(data.error);
        }
        
        // Update the price display with 2 decimal places
        const currentPrice = data.current_price || config.basePrice;
        const nextPrice = data.next_price || (config.basePrice * config.priceMultiplier);
        
        // Store price window information
        currentState.priceId = data.price_id;
        currentState.priceWindow = data.window_start;
        currentState.currentPrice = currentPrice;
        currentState.price = currentPrice;
        currentState.nextPrice = nextPrice;
        
        // Update UI
        elements.priceAmount.textContent = currentPrice.toFixed(2);
        if (elements.nextPrice) {
            elements.nextPrice.textContent = nextPrice.toFixed(2);
        }
        
        // Start the countdown timer using the server's window start time
        if (data.window_start) {
            const windowStart = new Date(data.window_start);
            const timeElapsed = Math.floor((Date.now() - windowStart.getTime()) / 1000);
            const timeLeft = Math.max(0, 300 - timeElapsed); // 5 minutes in seconds
            startPriceUpdateTimer(timeLeft);
        } else {
            startPriceUpdateTimer(300);
        }
        
        return data;
    } catch (error) {
        console.error('Error fetching price:', error);
        elements.priceAmount.textContent = 'Current Price loading...';
        if (elements.nextPrice) {
            elements.nextPrice.textContent = 'Next Price loading...';
        }
        currentState.currentPrice = null;
        currentState.price = null;
        currentState.nextPrice = null;
        currentState.priceId = null;
        currentState.priceWindow = null;
        
        startPriceUpdateTimer(300);
        
        return { error: error.message };
    }
}

async function refreshModalPrice() {
    const priceData = await fetchCurrentPrice();
    if (priceData.error) return;

    const modalPrice = document.getElementById('modal-price');
    const priceAlert = document.getElementById('price-change-alert');
    const submitBtn = document.getElementById('submit-ad-btn');
    
    if (modalPrice) {
        modalPrice.textContent = priceData.current_price.toFixed(2);
    }
    
    if (priceAlert) {
        priceAlert.classList.add('d-none');
    }
    
    if (submitBtn) {
        submitBtn.disabled = false;
    }
}

async function updateDisplaysAfterPayment(newAd) {
    console.log('Updating displays after payment...');
    let retries = 0;
    const maxRetries = 3;
    
    while (retries < maxRetries) {
        try {
            // Wait a moment for database propagation
            await new Promise(resolve => setTimeout(resolve, 500 * (retries + 1)));
            
            // Fetch fresh data
            await Promise.all([
                fetchCurrentPrice(),
                fetchPreviousAds()
            ]);
            
            console.log('Displays updated successfully');
            return;
        } catch (error) {
            console.error(`Update attempt ${retries + 1} failed:`, error);
            retries++;
            
            if (retries === maxRetries) {
                console.error('Failed to update displays after maximum retries');
                showAlert('warning', 'The page may need to be refreshed to see your new ad.');
            }
        }
    }
}

async function processPayment(adText, adUrl) {
    let paymentIntent;
    const alertContainer = document.getElementById('ad-form-alert');
    const alertMessage = alertContainer.querySelector('.alert-message');
    
    try {
        elements.submitAdBtn.disabled = true;
        elements.submitAdBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Processing...';

        // Get a fresh price window before starting payment process
        console.log('Getting fresh price window...');
        const priceData = await fetchCurrentPrice();
        if (priceData.error) throw new Error('Failed to get current price');
        
        // Update modal price display
        const modalPrice = document.getElementById('modal-price');
        if (modalPrice) {
            modalPrice.textContent = priceData.current_price.toFixed(2);
        }

        // Create payment intent with fresh price window
        console.log('Creating payment intent...');
        const createPaymentResponse = await fetch(`${config.apiUrl}/api/create-payment-intent`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                amount: currentState.currentPrice,
                userId: currentState.user.id,
                priceId: currentState.priceId,
                priceWindow: currentState.priceWindow
            })
        });

        if (!createPaymentResponse.ok) {
            const errorData = await createPaymentResponse.json();
            console.error('Payment intent creation failed:', errorData);
            throw new Error(errorData.error || 'Failed to create payment intent');
        }

        const { clientSecret } = await createPaymentResponse.json();
        console.log('Payment intent created successfully');

        // Confirm card payment
        console.log('Confirming card payment...');
        const result = await currentState.stripe.confirmCardPayment(clientSecret, {
            payment_method: {
                card: currentState.card,
                billing_details: {
                    email: currentState.user.email
                }
            }
        });

        if (result.error) {
            console.error('Card payment confirmation failed:', result.error);
            throw new Error(result.error.message);
        }

        paymentIntent = result.paymentIntent;
        if (!paymentIntent) {
            throw new Error('Payment failed: No payment confirmation received');
        }
        console.log('Card payment confirmed successfully');

        // Process the payment and create ad
        console.log('Processing payment and creating ad...');
        const processResponse = await fetch(`${config.apiUrl}/api/process-payment`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                paymentIntentId: paymentIntent.id,
                adText,
                adUrl,
                userId: currentState.user.id,
                priceId: currentState.priceId,
                priceWindow: currentState.priceWindow
            })
        });

        if (!processResponse.ok) {
            const errorData = await processResponse.json();
            console.error('Payment processing failed:', errorData);
            throw new Error(`Failed to process payment: ${errorData.error || 'Unknown error'}`);
        }

        const { success, ad } = await processResponse.json();
        if (!success) throw new Error('Failed to process payment: Server returned unsuccessful status');

        console.log('Payment processed and ad created successfully:', ad);

        // Show success message and reset form
        showAlert('success', 'Payment successful! Your ad has been posted.');
        elements.adInput.value = '';
        elements.adUrl.value = '';
        elements.charCount.textContent = '0';
        
        // Log the current Previous Ads state
        console.log('Fetching Previous Ads data before page refresh...');
        const { data: currentAds, error: adsError } = await currentState.supabaseClient
            .from('ads')
            .select('*')
            .eq('active', true)
            .order('created_at', { ascending: false })
            .limit(7);
            
        if (adsError) {
            console.error('Error fetching ads for logging:', adsError);
        } else {
            console.log('Current ads in database:', currentAds);
            console.log('Most recent ad:', currentAds[0]);
            console.log('Previous ads:', currentAds.slice(1));
        }
        
        // Close the modal
        elements.adModal.hide();
        
        // Wait a brief moment for the modal to close and alert to show
        setTimeout(() => {
            console.log('Refreshing page to show new ad...');
            window.location.reload();
        }, 1000);

        return ad;
    } catch (error) {
        console.error('Payment processing error:', error);
        alertMessage.textContent = error.message;
        alertContainer.classList.remove('d-none');
        throw error;
    } finally {
        elements.submitAdBtn.disabled = false;
        elements.submitAdBtn.innerHTML = '<i class="fas fa-check me-2"></i>Submit & Pay';
    }
}

// Helper function to show alerts
function showAlert(type, message) {
    const alertDiv = document.createElement('div');
    alertDiv.className = `alert alert-${type} alert-dismissible fade show m-3`;
    alertDiv.innerHTML = `
        <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2"></i>
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    document.body.insertBefore(alertDiv, document.body.firstChild);
    
    // Remove the alert after 5 seconds
    setTimeout(() => {
        alertDiv.remove();
    }, 5000);
}

// Authentication Functions
async function handleEmailAuth(event) {
    event.preventDefault();
    const email = elements.emailInput.value.trim().toLowerCase();
    const password = elements.passwordInput.value;
    const errorDisplay = document.getElementById('auth-error');
    
    // Clear any existing errors
    errorDisplay.classList.add('d-none');
    errorDisplay.textContent = '';

    try {
        elements.authSubmitBtn.disabled = true;
        elements.authSubmitBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Processing...';

        // Validate email
        if (!email || !password) {
            throw new Error('Please enter both email and password');
        }

        console.log('Attempting authentication...');
        let result;
        
        if (currentState.isRegistering) {
            // Validate password confirmation
            const confirmPassword = elements.confirmPasswordInput.value;
            if (password !== confirmPassword) {
                throw new Error('Passwords do not match');
            }

            // Register new user
            console.log('Registering new user...');
            result = await currentState.supabaseClient.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: `${config.siteUrl}/`
                }
            });
        } else {
            // Login existing user
            console.log('Logging in existing user...');
            result = await currentState.supabaseClient.auth.signInWithPassword({
                email,
                password
            });
        }

        console.log('Auth result:', result.error ? 'Error' : 'Success');

        if (result.error) {
            throw new Error(result.error.message);
        }

        if (result.data?.user) {
            if (currentState.isRegistering) {
                alert('Registration successful! Please check your email to verify your account.');
            }
        elements.authModal.hide();
        elements.authForm.reset();
        }
        
    } catch (error) {
        console.error('Authentication error:', error);
        errorDisplay.textContent = error.message;
        errorDisplay.classList.remove('d-none');
    } finally {
        elements.authSubmitBtn.disabled = false;
        elements.authSubmitBtn.innerHTML = currentState.isRegistering ? 'Register' : 'Login';
    }
}

async function handleGoogleAuth() {
    try {
        const { data, error } = await currentState.supabaseClient.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin,
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent'
                }
            }
        });

        if (error) throw error;
        elements.authModal.hide();
        
    } catch (error) {
        console.error('Authentication error:', error);
        alert('Authentication failed. Please try again.');
    }
}

function toggleAuthMode() {
    console.log('Toggling auth mode. Current state:', currentState.isRegistering);
    
    // Update modal title and button text
    elements.authModalTitle.textContent = currentState.isRegistering ? 'Register' : 'Login';
    elements.authSubmitBtn.textContent = currentState.isRegistering ? 'Register' : 'Login';
    
    // Update toggle link text
    elements.toggleAuthMode.textContent = currentState.isRegistering ? 
        'Already have an account? Login' : 'Don\'t have an account? Register';
    
    // Update description text
    elements.authDescription.textContent = currentState.isRegistering ?
        'Create an account to start posting ads and earning from the revenue sharing system.' :
        'Sign in to post ads and earn from the revenue sharing system.';
    
    // Show/hide confirm password field
    if (currentState.isRegistering) {
        elements.registerFields.classList.remove('d-none');
        elements.confirmPasswordInput.required = true;
    } else {
        elements.registerFields.classList.add('d-none');
        elements.confirmPasswordInput.required = false;
        elements.confirmPasswordInput.value = ''; // Clear confirm password when switching to login
    }
    
    console.log('Auth mode toggled. New state:', currentState.isRegistering);
}

function updateUIForAuthState(user) {
    console.log('Updating UI for auth state:', user);
    
    if (user) {
        // User is logged in
        console.log('User is logged in:', user.email);
        
        // Hide login/register buttons
        const authButtons = document.querySelectorAll('#auth-buttons > button');
        authButtons.forEach(button => button.classList.add('d-none'));
        
        // Show user menu
        const userMenu = document.getElementById('user-menu');
        userMenu.classList.remove('d-none');
        
        // Update email display
        const userEmail = document.getElementById('user-email');
        userEmail.textContent = user.email;
        
        // Enable post ad button
        const postAdBtn = document.getElementById('post-ad-btn');
        postAdBtn.disabled = false;
        
        console.log('UI updated for logged in state');
    } else {
        // User is logged out
        console.log('User is logged out');
        
        // Show login/register buttons
        const authButtons = document.querySelectorAll('#auth-buttons > button');
        authButtons.forEach(button => button.classList.remove('d-none'));
        
        // Hide user menu
        const userMenu = document.getElementById('user-menu');
        userMenu.classList.add('d-none');
        
        // Clear email display
        const userEmail = document.getElementById('user-email');
        userEmail.textContent = '';
        
        // Disable post ad button
        const postAdBtn = document.getElementById('post-ad-btn');
        postAdBtn.disabled = true;
        
        console.log('UI updated for logged out state');
    }
}

// Event Listeners
function setupEventListeners() {
    // Ad modal handlers
    const adModal = document.getElementById('ad-modal');
    
    adModal.addEventListener('show.bs.modal', async () => {
        try {
            // Get fresh price and store it immediately
            const priceData = await fetchCurrentPrice();
            if (priceData.error) throw new Error(priceData.error);
            
            // Update modal price display
            const modalPrice = document.getElementById('modal-price');
            if (modalPrice) {
                modalPrice.textContent = priceData.current_price.toFixed(2);
            }
            
            // Mount Stripe Card Element
            const cardElement = currentState.stripe.elements().create('card', {
                style: {
                    base: {
                        fontSize: '16px',
                        color: '#2c3e50',
                        '::placeholder': {
                            color: '#aab7c4'
                        }
                    },
                    invalid: {
                        color: '#dc3545',
                        iconColor: '#dc3545'
                    }
                }
            });
            
            cardElement.mount('#card-element');
            currentState.card = cardElement;
            
            // Handle card errors
            cardElement.on('change', ({error}) => {
                const displayError = document.getElementById('card-errors');
                if (error) {
                    displayError.textContent = error.message;
                } else {
                    displayError.textContent = '';
                }
            });
        } catch (error) {
            console.error('Error initializing modal:', error);
            showAlert('error', 'Failed to load current price. Please try again.');
            elements.adModal.hide();
        }
    });
    
    adModal.addEventListener('hidden.bs.modal', () => {
        // Clear any intervals that might have been set
        if (currentState.modalPriceInterval) {
            clearInterval(currentState.modalPriceInterval);
            currentState.modalPriceInterval = null;
        }

        // Unmount Stripe Card Element
        if (currentState.card) {
            currentState.card.unmount();
            currentState.card = null;
        }

        // Reset form
        elements.adInput.value = '';
        elements.adUrl.value = '';
        elements.charCount.textContent = '0';
        document.getElementById('card-errors').textContent = '';
        
        // Hide any alerts
        const alertContainer = document.getElementById('ad-form-alert');
        if (alertContainer) {
            alertContainer.classList.add('d-none');
        }
        const priceAlert = document.getElementById('price-change-alert');
        if (priceAlert) {
            priceAlert.classList.add('d-none');
        }
    });

    // Auth modal reset handler
    const authModal = document.getElementById('authModal');
    const modalBackdrop = document.querySelector('.modal-backdrop');
    
    // Handle modal show
    authModal.addEventListener('show.bs.modal', () => {
        // Set initial focus to email input
        setTimeout(() => {
            elements.emailInput.focus();
        }, 100);
    });

    // Handle modal shown (after animation)
    authModal.addEventListener('shown.bs.modal', () => {
        document.body.classList.add('modal-open');
        if (modalBackdrop) {
            modalBackdrop.setAttribute('aria-hidden', 'true');
        }
    });

    // Handle modal hide
    authModal.addEventListener('hide.bs.modal', () => {
        // Store the button that should receive focus
        const triggerButton = currentState.isRegistering ? 
            elements.registerBtn : elements.loginBtn;
        currentState.lastFocusedElement = triggerButton;
    });

    // Handle modal hidden (after animation)
    authModal.addEventListener('hidden.bs.modal', () => {
        // Reset form and auth state
        elements.authForm.reset();
        if (currentState.isRegistering) {
            currentState.isRegistering = false;
            toggleAuthMode();
        }
        
        // Clean up modal state
        document.body.classList.remove('modal-open');
        if (modalBackdrop) {
            modalBackdrop.remove();
        }
        
        // Return focus
        if (currentState.lastFocusedElement) {
            currentState.lastFocusedElement.focus();
        }
    });
    
    // Login button
elements.loginBtn.addEventListener('click', () => {
    currentState.isRegistering = false;
    toggleAuthMode();
        elements.authModal.show();
});

    // Register button
elements.registerBtn.addEventListener('click', () => {
    currentState.isRegistering = true;
    toggleAuthMode();
        elements.authModal.show();
});

    // Auth form submission
elements.authForm.addEventListener('submit', handleEmailAuth);
    
    // Auth mode toggle
elements.toggleAuthMode.addEventListener('click', (e) => {
    e.preventDefault();
        currentState.isRegistering = !currentState.isRegistering;
    toggleAuthMode();
});
    
    // Google auth button
elements.googleAuthBtn.addEventListener('click', handleGoogleAuth);

    // Logout button
    elements.logoutBtn.addEventListener('click', async (e) => {
        e.preventDefault();
    try {
            await currentState.supabaseClient.auth.signOut();
    } catch (error) {
            console.error('Error signing out:', error);
        }
    });
    
    // Ad input character counter
    elements.adInput.addEventListener('input', () => {
        const length = elements.adInput.value.length;
        elements.charCount.textContent = length;
    });
    
    // Post ad button
    elements.postAdBtn.addEventListener('click', () => {
        if (!currentState.user) {
            elements.authModal.show();
            return;
        }
        elements.adModal.show();
    });
    
    // Submit ad button
    elements.submitAdBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        
        const form = document.getElementById('ad-form');
        const alertContainer = document.getElementById('ad-form-alert');
        const alertMessage = alertContainer.querySelector('.alert-message');
        
        // Hide any previous alerts
        alertContainer.classList.add('d-none');
        
        // Reset custom validity
        elements.adInput.setCustomValidity('');
        elements.adUrl.setCustomValidity('');
        
        form.classList.add('was-validated');
        
        if (!form.checkValidity()) {
            if (!elements.adInput.value.trim()) {
                alertMessage.textContent = 'Please enter your ad text.';
            } else if (!elements.adUrl.value.trim()) {
                alertMessage.textContent = 'Please enter a URL for your ad.';
            }
            alertContainer.classList.remove('d-none');
            return;
        }
        
        const adText = elements.adInput.value.trim();
        const adUrl = elements.adUrl.value.trim();
        
        try {
            // Validate URL format
            new URL(adUrl);
            await processPayment(adText, adUrl);
        } catch (error) {
            if (error instanceof TypeError) {
                elements.adUrl.setCustomValidity('Please enter a valid URL (e.g., https://example.com)');
                alertMessage.textContent = 'Please enter a valid URL starting with http:// or https://';
                alertContainer.classList.remove('d-none');
                form.reportValidity();
            } else {
                // For other errors, show in the alert container
                alertMessage.textContent = error.message;
                alertContainer.classList.remove('d-none');
            }
        }
    });

    // Profile buttons
    elements.viewProfileBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = '/profile.html';
});

elements.myAdsBtn.addEventListener('click', () => {
    alert('Ad history coming soon!');
});

elements.earningsBtn.addEventListener('click', () => {
    alert('Earnings dashboard coming soon!');
});

    // Admin Panel Event Listeners
    const adminPanel = document.getElementById('admin-panel');
    const adminToggle = document.getElementById('admin-toggle');
    const adminClose = document.querySelector('.admin-close');

    if (adminToggle) {
        adminToggle.addEventListener('click', () => {
            console.log('Admin toggle clicked');
            adminPanel.classList.toggle('open');
        });
    }

    if (adminClose) {
        adminClose.addEventListener('click', () => {
            console.log('Admin close clicked');
            adminPanel.classList.remove('open');
        });
    }
}

// Admin Panel Functions
async function setupAdminPanel() {
    console.log('Setting up admin panel...');
    const adminLink = document.getElementById('admin-link');
    
    // Hide admin link by default
    if (adminLink) adminLink.classList.add('d-none');
    
    // If no user is logged in, return early
    if (!currentState.user) {
        console.log('No user logged in, hiding admin link');
        return;
    }
    
    // Check if user is admin
    try {
        console.log('Checking admin status for user:', currentState.user.email);
        const { data: adminData, error: adminError } = await currentState.supabaseClient
            .from('administrators')
            .select('*')
            .eq('user_id', currentState.user.id)
            .single();
        
        if (adminError || !adminData) {
            console.log('User is not an admin:', currentState.user.email);
            return;
        }
        
        console.log('User is confirmed as admin:', currentState.user.email);
        
        // Show admin link only after confirming admin status
        if (adminLink) adminLink.classList.remove('d-none');
    } catch (error) {
        console.error('Error setting up admin panel:', error);
        if (adminLink) adminLink.classList.add('d-none');
    }
}

async function loadAdminData() {
    const adsList = document.getElementById('admin-ads-list');
    
    try {
        const { data: ads, error } = await currentState.supabaseClient
            .from('ads')
            .select(`
                *,
                payments (
                    amount,
                    status,
                    stripe_payment_id
                )
            `)
            .order('created_at', { ascending: false });
            
        if (error) throw error;
        
        adsList.innerHTML = ads.map(ad => {
            const payment = ad.payments?.[0];
            return `
                <tr>
                    <td class="text-truncate" style="max-width: 200px;">${ad.text}</td>
                    <td>$${payment?.amount?.toFixed(2) || '0.00'}</td>
                    <td>${formatDate(ad.created_at)}</td>
                    <td>
                        <div class="btn-group">
                            <button class="btn btn-sm btn-${ad.active ? 'warning' : 'success'} toggle-ad-btn" 
                                    data-ad-id="${ad.id}" data-active="${ad.active}">
                                ${ad.active ? 'Block' : 'Unblock'}
                            </button>
                            <button class="btn btn-sm btn-danger delete-ad-btn" data-ad-id="${ad.id}">
                                Delete
                            </button>
                        </div>
                    </td>
                </tr>
            `;
        }).join('');
        
        // Add event listeners for action buttons
        adsList.querySelectorAll('.toggle-ad-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                const adId = btn.dataset.adId;
                const currentlyActive = btn.dataset.active === 'true';
                
                try {
                    const { error } = await currentState.supabaseClient
                        .from('ads')
                        .update({ active: !currentlyActive })
                        .eq('id', adId);
                        
                    if (error) throw error;
                    
                    showAlert('success', `Ad ${currentlyActive ? 'blocked' : 'unblocked'} successfully`);
                    loadAdminData();
                    fetchPreviousAds();
                } catch (error) {
                    console.error('Error toggling ad:', error);
                    showAlert('error', 'Failed to update ad status');
                }
            });
        });
        
        adsList.querySelectorAll('.delete-ad-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                if (!confirm('Are you sure you want to delete this ad?')) return;
                
                const adId = btn.dataset.adId;
                try {
                    const { error } = await currentState.supabaseClient
                        .from('ads')
                        .delete()
                        .eq('id', adId);
                        
                    if (error) throw error;
                    
                    showAlert('success', 'Ad deleted successfully');
                    loadAdminData();
                    fetchPreviousAds();
                } catch (error) {
                    console.error('Error deleting ad:', error);
                    showAlert('error', 'Failed to delete ad');
                }
            });
        });
    } catch (error) {
        console.error('Error loading admin data:', error);
        adsList.innerHTML = '<tr><td colspan="4" class="text-center text-muted">Failed to load ads</td></tr>';
    }
}

// Add admin check function
async function checkAndUpdateAdminStatus(userId) {
    try {
        if (!userId || !currentState.supabaseClient) {
            console.log('Missing userId or supabaseClient for admin check');
            return false;
        }
        
        console.log('Checking admin status for user:', userId);
        const { data: adminData, error: adminError } = await currentState.supabaseClient
            .from('administrators')
            .select('*')
            .eq('user_id', userId);

        const adminLink = document.getElementById('admin-link');
        
        // If there's a real error (not just no rows found), log it
        if (adminError && adminError.code !== 'PGRST116') {
            console.error('Error checking admin status:', adminError);
            adminLink?.classList.add('d-none');
            return false;
        }

        // Check if we found an admin record
        if (adminData && adminData.length > 0) {
            console.log('User confirmed as admin');
            adminLink?.classList.remove('d-none');
            return true;
        } else {
            console.log('User is not an admin');
            adminLink?.classList.add('d-none');
            return false;
        }
    } catch (error) {
        console.error('Error checking admin status:', error);
        return false;
    }
}

// Update handleAuthStateChange function
async function handleAuthStateChange(session) {
    console.log('Auth state changed:', session?.user?.email);
    
    if (session) {
        currentState.user = session.user;
        elements.userEmail.textContent = session.user.email;
        elements.userMenu.classList.remove('d-none');
        elements.loginBtn.classList.add('d-none');
        elements.registerBtn.classList.add('d-none');
        
        // Check admin status immediately after login
        await checkAndUpdateAdminStatus(session.user.id);
        
        // Update UI and fetch data
        await Promise.all([
            fetchCurrentPrice(),
            fetchLatestAd(),
            fetchPreviousAds()
        ]);
    } else {
        currentState.user = null;
        elements.userEmail.textContent = '';
        elements.userMenu.classList.add('d-none');
        elements.loginBtn.classList.remove('d-none');
        elements.registerBtn.classList.remove('d-none');
        const adminLink = document.getElementById('admin-link');
        adminLink?.classList.add('d-none');
    }
}

// Update init function
async function init() {
    try {
        console.log('Starting app initialization...');
        
        // Wait for supabaseClient to be ready
        let attempts = 0;
        while (!window.supabaseClient && attempts < 50) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }

        if (!window.supabaseClient) {
            throw new Error('Database client not initialized');
        }

        // Store supabase client in state
        currentState.supabaseClient = window.supabaseClient;
        console.log('Supabase client stored in state');

        // Set up auth state change listener first
        currentState.supabaseClient.auth.onAuthStateChange((event, session) => {
            console.log('Auth state changed:', event, session?.user?.email);
            handleAuthStateChange(session);
        });

        // Get current session
        console.log('Getting current session...');
        const { data: { session }, error: sessionError } = await currentState.supabaseClient.auth.getSession();
        if (sessionError) {
            console.error('Session error:', sessionError);
            throw sessionError;
        }

        if (session) {
            console.log('Session found, initializing with user:', session.user.email);
            await handleAuthStateChange(session);
        } else {
            console.log('No session found, initializing without user');
            await handleAuthStateChange(null);
        }

        // Initialize components
        console.log('Initializing components...');
        await Promise.all([
            fetchCurrentPrice(),
            fetchLatestAd(),
            fetchPreviousAds()
        ]);

        // Set up event listeners
        setupEventListeners();
        setupRealtimeUpdates();

        console.log('App initialization complete');
    } catch (error) {
        console.error('Initialization error:', error);
        showAlert('danger', 'Failed to initialize the application');
    }
}

// Start initialization when DOM is loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
init(); 
}

function setupRealtimeUpdates() {
    try {
        // Unsubscribe from any existing channels
        if (currentState.channel) {
            currentState.channel.unsubscribe();
        }

        // Set up real-time subscription for ads
        currentState.channel = currentState.supabaseClient
            .channel('ads-channel')
            .on('postgres_changes', 
                { 
                    event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
                    schema: 'public',
                    table: 'ads'
                },
                async (payload) => {
                    console.log('Received real-time update:', payload);
                    
                    // Add a small delay to ensure database consistency
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                    // Refresh all displays
                    await Promise.all([
                        fetchCurrentPrice(),
                        fetchPreviousAds()
                    ]);
                }
            )
            .subscribe((status) => {
                console.log('Real-time subscription status:', status);
            });

        console.log('Real-time channel subscription established');
        
        // Initialize viewer count
        currentState.viewers = 1;
        elements.viewerCount.textContent = currentState.viewers;
        
        // Set up presence channel for viewer counting
        const presenceChannel = currentState.supabaseClient
            .channel('online-users')
            .on('presence', { event: 'sync' }, () => {
                const presenceState = presenceChannel.presenceState();
                const totalViewers = Object.keys(presenceState).length;
                currentState.viewers = Math.max(1, totalViewers);
                elements.viewerCount.textContent = currentState.viewers;
            })
            .subscribe(async (status) => {
                if (status === 'SUBSCRIBED') {
                    await presenceChannel.track({ user_id: crypto.randomUUID() });
                }
            });
    } catch (error) {
        console.error('Error setting up real-time subscription:', error);
    }
}

// Helper Functions
function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString();
}

// Function to fetch previous ads
async function fetchPreviousAds() {
    try {
        // First check if we have the required elements
        const previousAdsList = document.getElementById('previous-ads-list');
        if (!previousAdsList) {
            console.warn('Previous ads list element not found');
            return;
        }

        // Check if Supabase client is initialized
        if (!currentState.supabaseClient) {
            throw new Error('Database client not initialized');
        }

        // First get the current ad's ID
        const { data: currentAd, error: currentAdError } = await currentState.supabaseClient
            .from('ads')
            .select('id')
            .eq('active', true)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

        if (currentAdError) {
            console.error('Error fetching current ad:', currentAdError);
            throw currentAdError;
        }

        console.log('Fetching previous ads...');
        const query = currentState.supabaseClient
            .from('ads')
            .select(`
                *,
                payments (
                    id,
                    amount,
                    status,
                    stripe_payment_id
                )
            `)
            .eq('active', true)
            .order('created_at', { ascending: false })
            .limit(11); // Get one extra to account for current ad

        // Exclude current ad if we have its ID
        if (currentAd?.id) {
            query.neq('id', currentAd.id);
        }

        const { data: ads, error } = await query;

        if (error) {
            console.error('Database error:', error);
            throw error;
        }

        if (!ads || ads.length === 0) {
            previousAdsList.innerHTML = `
                <div class="text-center text-muted py-4">
                    <i class="fas fa-history me-2"></i>
                    No previous ads found
                </div>
            `;
            return;
        }

        // Limit to 10 ads after excluding current ad
        const previousAds = ads.slice(0, 10);

        previousAdsList.innerHTML = previousAds.map((ad, index) => {
            const payment = ad.payments?.[0];
            const amount = payment?.amount ? parseFloat(payment.amount).toFixed(2) : '0.00';
            
            return `
                <a href="${ad.url}" target="_blank" rel="noopener noreferrer" class="list-group-item list-group-item-action">
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="flex-grow-1 me-3">
                            <div class="list-group-item-title text-break">${ad.text}</div>
                            <div class="list-group-item-meta">
                                <i class="fas fa-clock me-1"></i>${formatRelativeTime(ad.created_at)}
                                <i class="fas fa-tag ms-3 me-1"></i>$${amount}
                            </div>
                        </div>
                        <span class="list-group-item-number flex-shrink-0">#${index + 1}</span>
                    </div>
                </a>
            `;
        }).join('');

    } catch (error) {
        console.error('Error in fetchPreviousAds:', error);
        const errorMessage = error.message || 'Failed to load previous ads';
        showAlert('danger', errorMessage);
        
        // Update the UI to show the error state
        const previousAdsList = document.getElementById('previous-ads-list');
        if (previousAdsList) {
            previousAdsList.innerHTML = `
                <div class="text-center text-danger py-4">
                    <i class="fas fa-exclamation-circle me-2"></i>
                    ${errorMessage}
                    <button class="btn btn-link text-danger" onclick="fetchPreviousAds()">
                        <i class="fas fa-sync-alt me-1"></i>Retry
                    </button>
                </div>
            `;
        }
    }
}

// Function to format relative time
function formatRelativeTime(timestamp) {
    const now = new Date();
    const then = new Date(timestamp);
    const diffInSeconds = Math.floor((now - then) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
}

// Function to update previous ads display
function updatePreviousAdsDisplay(ads) {
    if (!elements.previousAdsList) return;

    if (!ads || ads.length === 0) {
        elements.previousAdsList.innerHTML = `
            <div class="text-center text-muted py-4">
                <i class="fas fa-history me-2"></i>
                No previous ads found
            </div>
        `;
        return;
    }

    const adsHtml = ads.map((ad, index) => `
        <a href="${ad.url}" target="_blank" rel="noopener noreferrer" class="list-group-item list-group-item-action">
            <div class="d-flex justify-content-between align-items-center">
                <div class="flex-grow-1 me-3">
                    <div class="list-group-item-title text-break">${ad.text}</div>
                    <div class="list-group-item-meta">
                        <i class="fas fa-clock me-1"></i>${formatRelativeTime(ad.created_at)}
                        <i class="fas fa-tag ms-3 me-1"></i>$${ad.price_paid.toFixed(2)}
                    </div>
                </div>
                <span class="list-group-item-number flex-shrink-0">#${index + 1}</span>
            </div>
        </a>
    `).join('');

    elements.previousAdsList.innerHTML = adsHtml;
} 